import { defineStore } from 'pinia';
import { ref } from 'vue';
import { supabase } from '../lib/supabaseClient';
import { useAuthStore } from './auth';
import { mapCampaignFromDb } from '../utils/mappers';
import { useSubscription } from '../composables/useSubscription';
import { AnalyticsEvents } from '../lib/analytics';
import { fetchAllPages } from '../utils/supabasePagination';

export const useCampaignStore = defineStore('campaigns', () => {
  const campaigns = ref([]);
  const loading = ref(false);
  const error = ref(null);
  
  // Cache control - prevent redundant fetches within a short time window
  let lastFetchTime = null;
  let fetchInProgress = false;
  const CACHE_DURATION_MS = 30000; // 30 seconds

  async function fetchCampaigns(forceRefresh = false) {
    // Prevent duplicate calls while already fetching
    if (fetchInProgress) {
      return;
    }
    
    // Check cache - skip fetch if data is fresh (unless forced)
    if (!forceRefresh && lastFetchTime && campaigns.value.length > 0) {
      const timeSinceLastFetch = Date.now() - lastFetchTime;
      if (timeSinceLastFetch < CACHE_DURATION_MS) {
        return;
      }
    }
    
    fetchInProgress = true;
    
    try {
      loading.value = true;
      error.value = null;
      
      // RLS handles organization filtering. Page through campaigns and cost logs
      // so neither silently truncates at Supabase's default 1000-row API cap.
      const { data: campaignsData, error: campaignsError } = await fetchAllPages((from, to) =>
        supabase
          .from('campaigns')
          .select('*')
          .eq('_deleted', false)
          .order('start_date', { ascending: false })
          .range(from, to)
      );

      if (campaignsError) throw campaignsError;

      const { data: costLogsData, error: costLogsError } = await fetchAllPages((from, to) =>
        supabase
          .from('campaign_cost_logs')
          .select('*')
          .eq('_deleted', false)
          .order('date', { ascending: false })
          .range(from, to)
      );

      if (costLogsError) throw costLogsError;

      // Map campaigns and attach their cost logs
      campaigns.value = (campaignsData || []).map(campaign =>
        mapCampaignFromDb(campaign, costLogsData || [])
      );
      lastFetchTime = Date.now();
    } catch (err) {
      error.value = 'Failed to fetch campaigns';
      console.error('Error:', err);
    } finally {
      loading.value = false;
      fetchInProgress = false;
    }
  }
  
  // Force refresh from server (invalidates cache)
  async function refreshCampaigns() {
    return fetchCampaigns(true);
  }

  async function createCampaign(campaign) {
    try {
      loading.value = true;
      error.value = null;
      
      const authStore = useAuthStore();
      if (!authStore.organizationId) throw new Error('No organization selected');

      // Check if a campaign with the same campaign_number already exists
      if (campaign.campaignNumber) {
        const { data: existingCampaign, error: checkError } = await supabase
          .from('campaigns')
          .select('id, name, campaign_number')
          .eq('campaign_number', campaign.campaignNumber)
          .eq('_deleted', false)
          .maybeSingle();

        if (checkError) throw checkError;

        if (existingCampaign) {
          const duplicateError = new Error(
            `A campaign with campaign number "${campaign.campaignNumber}" already exists.`
          );
          duplicateError.code = 'DUPLICATE_CAMPAIGN_NUMBER';
          throw duplicateError;
        }
      }

      const { data, error: err } = await supabase
        .from('campaigns')
        .insert([{
          name: campaign.name,
          notes: campaign.notes,
          start_date: campaign.startDate.toISOString(),
          end_date: campaign.endDate.toISOString(),
          budget: campaign.budget || null,
          campaign_number: campaign.campaignNumber || null,
          mailers_sent: campaign.mailersSent || null,
          mailer_spend: campaign.mailerSpend || null,
          presentation_name: campaign.presentationName || null,
          office_id: campaign.officeId || null,
          organization_id: authStore.organizationId,
          user_id: authStore.user?.id
        }])
        .select()
        .single();

      if (err) throw err;

      const newCampaign = mapCampaignFromDb(data, []);
      campaigns.value.push(newCampaign);
      AnalyticsEvents.campaignCreated();

      // Invalidate subscription cache so canCreate reflects the new campaign count
      try {
        const { refreshUsage } = useSubscription();
        await refreshUsage(true);
      } catch (_) { /* non-fatal */ }

      return newCampaign;
    } catch (err) {
      if (err.code === 'DUPLICATE_CAMPAIGN_NUMBER') {
        error.value = err.message;
      } else {
        error.value = 'Failed to create campaign';
      }
      console.error('Error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateCampaign(campaign) {
    try {
      loading.value = true;
      error.value = null;

      const { error: err } = await supabase
        .from('campaigns')
        .update({
          name: campaign.name,
          notes: campaign.notes,
          start_date: campaign.startDate.toISOString(),
          end_date: campaign.endDate.toISOString(),
          budget: campaign.budget || null,
          campaign_number: campaign.campaignNumber || null,
          mailers_sent: campaign.mailersSent || null,
          mailer_spend: campaign.mailerSpend || null,
          presentation_name: campaign.presentationName || null,
          office_id: campaign.officeId || null
        })
        .eq('id', campaign.id);

      if (err) throw err;

      const index = campaigns.value.findIndex(c => c.id === campaign.id);
      if (index !== -1) {
        campaigns.value[index] = campaign;
      }
    } catch (err) {
      error.value = 'Failed to update campaign';
      console.error('Error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteCampaign(id) {
    try {
      loading.value = true;
      error.value = null;

      // First check if the campaign exists
      const { data: existingCampaign, error: checkError } = await supabase
        .from('campaigns')
        .select('id')
        .eq('id', id)
        .eq('_deleted', false)
        .single();

      if (checkError) throw checkError;
      if (!existingCampaign) throw new Error('Campaign not found');

      // Soft delete the campaign using RPC to bypass potential RLS issues
      const { error: deleteError } = await supabase
        .rpc('soft_delete_campaign', { p_campaign_id: id });

      if (deleteError) throw deleteError;

      // Update local state
      campaigns.value = campaigns.value.filter(campaign => campaign.id !== id);
    } catch (err) {
      error.value = 'Failed to delete campaign';
      console.error('Error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function addCostLogEntry(campaignId, entry) {
    try {
      loading.value = true;
      error.value = null;
      
      const authStore = useAuthStore();
      if (!authStore.organizationId) throw new Error('No organization selected');

      const { error: err } = await supabase
        .from('campaign_cost_logs')
        .insert([{
          campaign_id: campaignId,
          amount: entry.amount,
          description: entry.description,
          date: entry.date.toISOString(),
          organization_id: authStore.organizationId
        }]);

      if (err) throw err;

      // Refresh campaigns to get updated cost log
      await fetchCampaigns();
    } catch (err) {
      error.value = 'Failed to add cost log entry';
      console.error('Error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    campaigns,
    loading,
    error,
    fetchCampaigns,
    refreshCampaigns,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    addCostLogEntry
  };
});
